import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import {
  PlannedTime,
  PlannedTimeFrequency,
  TimeOff,
} from "@/helpers/project/typesDefinition";

dayjs.extend(isBetween);

type RecurringPlanningItem = {
  startDate: string | Date;
  endDate: string | Date;
  frequency?: PlannedTimeFrequency | string | null;
  repeatEndDate?: string | Date | null;
};

type RecurrenceConfig = {
  unit: "week" | "month" | "year";
  interval: number;
};

/**
 * Calculates the number of times a recurring event will repeat based on the frequency and end date.
 * Returns the count as an indication for the user.
 */
export const calculateRepeatCount = (
  startDate: string,
  repeatEndDate: string | null,
  frequency: PlannedTimeFrequency | null,
): number => {
  if (
    !repeatEndDate ||
    !frequency ||
    frequency === PlannedTimeFrequency.NO_REPEAT
  ) {
    return 0;
  }

  const start = dayjs(startDate).startOf("day");
  const end = dayjs(repeatEndDate).startOf("day");

  if (end.isBefore(start) || end.isSame(start, "day")) {
    return 0;
  }

  switch (frequency) {
    case PlannedTimeFrequency.WEEKLY:
      return Math.floor(end.diff(start, "week", true)) + 1;

    case PlannedTimeFrequency.EVERY_2_WEEKS:
      return Math.floor(end.diff(start, "week", true) / 2) + 1;

    case PlannedTimeFrequency.EVERY_3_WEEKS:
      return Math.floor(end.diff(start, "week", true) / 3) + 1;

    case PlannedTimeFrequency.EVERY_6_WEEKS:
      return Math.floor(end.diff(start, "week", true) / 6) + 1;

    case PlannedTimeFrequency.MONTHLY:
      return Math.floor(end.diff(start, "month", true)) + 1;

    case PlannedTimeFrequency.EVERY_3_MONTHS:
      return Math.floor(end.diff(start, "month", true) / 3) + 1;

    case PlannedTimeFrequency.EVERY_6_MONTHS:
      return Math.floor(end.diff(start, "month", true) / 6) + 1;

    case PlannedTimeFrequency.YEARLY:
      return Math.floor(end.diff(start, "year", true)) + 1;

    default:
      return 0;
  }
};

const getRecurrenceConfig = (
  frequency?: PlannedTimeFrequency | string | null,
): RecurrenceConfig | null => {
  switch (frequency) {
    case PlannedTimeFrequency.WEEKLY:
      return {
        unit: "week",
        interval: 1,
      };

    case PlannedTimeFrequency.EVERY_2_WEEKS:
      return {
        unit: "week",
        interval: 2,
      };

    case PlannedTimeFrequency.EVERY_3_WEEKS:
      return {
        unit: "week",
        interval: 3,
      };

    case PlannedTimeFrequency.EVERY_6_WEEKS:
      return {
        unit: "week",
        interval: 6,
      };

    case PlannedTimeFrequency.MONTHLY:
      return {
        unit: "month",
        interval: 1,
      };

    case PlannedTimeFrequency.EVERY_3_MONTHS:
      return {
        unit: "month",
        interval: 3,
      };

    case PlannedTimeFrequency.EVERY_6_MONTHS:
      return {
        unit: "month",
        interval: 6,
      };

    case PlannedTimeFrequency.YEARLY:
      return {
        unit: "year",
        interval: 1,
      };

    default:
      return null;
  }
};

const getWeekdayOccurrenceInMonth = (date: dayjs.Dayjs) => {
  return Math.ceil(date.date() / 7);
};

const getDateForWeekdayOccurrenceInMonth = (
  year: number,
  month: number,
  dayOfWeek: number,
  occurrence: number,
) => {
  const firstDayOfMonth = dayjs()
    .year(year)
    .month(month)
    .date(1)
    .startOf("day");

  const daysUntilTargetDay = (dayOfWeek - firstDayOfMonth.day() + 7) % 7;

  const targetDate = firstDayOfMonth
    .add(daysUntilTargetDay, "day")
    .add(occurrence - 1, "week");

  if (targetDate.month() !== month) {
    return null;
  }

  return targetDate;
};

const getDiffFromStart = (
  currentDate: dayjs.Dayjs,
  startDate: dayjs.Dayjs,
  unit: "week" | "month" | "year",
) => {
  if (unit === "week") {
    return currentDate.startOf("week").diff(startDate.startOf("week"), "week");
  }

  if (unit === "month") {
    return (
      (currentDate.year() - startDate.year()) * 12 +
      currentDate.month() -
      startDate.month()
    );
  }

  return currentDate.year() - startDate.year();
};

const getWeeklyOccurrenceStartDate = (
  startDate: dayjs.Dayjs,
  diffFromStart: number,
) => {
  return startDate.add(diffFromStart, "week").startOf("day");
};

const getMonthlyOccurrenceStartDate = (
  startDate: dayjs.Dayjs,
  diffFromStart: number,
) => {
  const targetMonth = startDate.add(diffFromStart, "month");
  const occurrence = getWeekdayOccurrenceInMonth(startDate);

  return getDateForWeekdayOccurrenceInMonth(
    targetMonth.year(),
    targetMonth.month(),
    startDate.day(),
    occurrence,
  );
};

const getYearlyOccurrenceStartDate = (
  startDate: dayjs.Dayjs,
  diffFromStart: number,
) => {
  return startDate.add(diffFromStart, "year").startOf("day");
};

const getOccurrenceStartDate = (
  startDate: dayjs.Dayjs,
  diffFromStart: number,
  unit: "week" | "month" | "year",
) => {
  if (unit === "week") {
    return getWeeklyOccurrenceStartDate(startDate, diffFromStart);
  }

  if (unit === "month") {
    return getMonthlyOccurrenceStartDate(startDate, diffFromStart);
  }

  return getYearlyOccurrenceStartDate(startDate, diffFromStart);
};

const isDateInRecurringItem = (date: string, item: RecurringPlanningItem) => {
  const currentDate = dayjs(date).startOf("day");
  const startDate = dayjs(item.startDate).startOf("day");
  const endDate = dayjs(item.endDate).startOf("day");
  const frequency = item.frequency ?? PlannedTimeFrequency.NO_REPEAT;
  const durationInDays = Math.max(endDate.diff(startDate, "day"), 0);

  if (currentDate.isBefore(startDate, "day")) {
    return false;
  }

  if (!frequency || frequency === PlannedTimeFrequency.NO_REPEAT) {
    return currentDate.isBetween(startDate, endDate, "day", "[]");
  }

  if (item.repeatEndDate) {
    const repeatEndDate = dayjs(item.repeatEndDate).startOf("day");

    if (currentDate.isAfter(repeatEndDate, "day")) {
      return false;
    }
  }

  const recurrenceConfig = getRecurrenceConfig(frequency);

  if (!recurrenceConfig) {
    return false;
  }

  const diffFromStart = getDiffFromStart(
    currentDate,
    startDate,
    recurrenceConfig.unit,
  );

  if (diffFromStart < 0) {
    return false;
  }

  if (diffFromStart % recurrenceConfig.interval !== 0) {
    return false;
  }

  const occurrenceStartDate = getOccurrenceStartDate(
    startDate,
    diffFromStart,
    recurrenceConfig.unit,
  );

  if (!occurrenceStartDate) {
    return false;
  }

  const occurrenceEndDate = occurrenceStartDate.add(durationInDays, "day");

  return currentDate.isBetween(
    occurrenceStartDate,
    occurrenceEndDate,
    "day",
    "[]",
  );
};

export const isDateInPlannedTime = (date: string, plannedTime: PlannedTime) => {
  return isDateInRecurringItem(date, plannedTime);
};

export const isDateInHoliday = (date: string, holiday: { date: string }) => {
  const currentDate = dayjs(date).startOf("day");
  const holidayDate = dayjs(holiday.date).startOf("day");

  return currentDate.isSame(holidayDate, "day");
};

export const isDateInTimeOff = (date: string, timeOff: TimeOff) => {
  return isDateInRecurringItem(date, timeOff);
};
